import './bootstrap';
// First-touch UTM/referrer capture; must run before any router redirect.
import './acquisition-init';
// Telegram Mini App one-time setup (expand + theme). No-op outside Telegram.
import './telegram-init';
// Site branding (examing.io palette + favicon) before the first render.
import './site-init';
import './i18n';

import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import App from './App';
import * as AuthModule from 'modules/auth';
import { Toaster } from 'components/ui/toaster';

import './index.css';

// Suppress harmless ResizeObserver errors (common browser quirk when ResizeObserver callbacks trigger layout changes)
// This error is harmless and doesn't affect functionality
const suppressResizeObserverError = () => {
  const originalError = console.error.bind(console);
  console.error = (...args: any[]) => {
    const errorMessage = args[0]?.toString() || '';
    if (
      errorMessage.includes('ResizeObserver loop completed with undelivered notifications') ||
      errorMessage.includes('ResizeObserver loop limit exceeded') ||
      errorMessage.includes('ResizeObserver loop')
    ) {
      return;
    }
    originalError(...args);
  };

  // Suppress in window error handler (capture phase)
  window.addEventListener(
    'error',
    (event) => {
      const message = event.message?.toString() || '';
      if (
        message.includes('ResizeObserver loop completed with undelivered notifications') ||
        message.includes('ResizeObserver loop limit exceeded') ||
        message.includes('ResizeObserver loop')
      ) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    },
    true
  );

  // Suppress unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.toString() || '';
    if (
      reason.includes('ResizeObserver') ||
      reason.includes('ResizeObserver loop')
    ) {
      event.preventDefault();
    }
  });
};

// Initialize error suppression
suppressResizeObserverError();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

const root = createRoot(document.getElementById('root')!);

// Google sign-in uses a server-side redirect flow (GET /api/auth/google), so no
// client-side Google SDK / provider is mounted here. The "Continue with Google"
// button hides itself when no client id is configured.
root.render(
  <React.StrictMode>
    <Suspense fallback={null}>
      <QueryClientProvider client={queryClient}>
        <AuthModule.Context.Provider>
          <App />
          <Toaster />
        </AuthModule.Context.Provider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Suspense>
  </React.StrictMode>
);
